/**
 * FloatingRulerButton - Floating button for ruler measurement tool
 *
 * Positioned at bottom-left of the map for quick access to distance measurement.
 */

import React from 'react';
import { Ruler } from 'lucide-react';

interface FloatingRulerButtonProps {
  isRulerMode: boolean;
  rulerPoints: [number, number][];
  onToggleRuler: () => void;
  formatDistance: (meters: number) => string;
  calculateTotalDistance: (points: [number, number][]) => number;
}

export function FloatingRulerButton({
  isRulerMode,
  rulerPoints,
  onToggleRuler,
  formatDistance,
  calculateTotalDistance,
}: FloatingRulerButtonProps) {
  return (
    <div className="absolute bottom-4 left-4 z-[1000] group">
      <button
        onClick={onToggleRuler}
        className={`w-12 h-12 rounded-lg flex items-center justify-center shadow-lg transition-all ${
          isRulerMode
            ? 'bg-blue-500 text-white hover:bg-blue-600'
            : 'bg-white text-gray-700 border-2 border-gray-300 hover:bg-gray-50 hover:border-blue-400'
        }`}
        title="Ruler"
      >
        <Ruler className="h-5 w-5" />
      </button>

      {/* Tooltip */}
      <div className="absolute left-14 bottom-2 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
        <div className="bg-gray-900 text-white text-xs px-2 py-1 rounded whitespace-nowrap">
          Ruler
        </div>
      </div>

      {/* Distance Display - Show when ruler is active and has points */}
      {isRulerMode && rulerPoints.length > 0 && (
        <div className="absolute left-14 bottom-0 bg-white border-2 border-blue-200 rounded-lg p-2 shadow-lg text-xs">
          <p className="font-semibold text-blue-600">
            {rulerPoints.length} point{rulerPoints.length !== 1 ? 's' : ''}
          </p>
          {rulerPoints.length > 1 && (
            <p className="font-mono text-blue-700 mt-1">
              {formatDistance(calculateTotalDistance(rulerPoints))}
            </p>
          )}
        </div>
      )}
    </div>
  );
}

export default FloatingRulerButton;
